import EventEmitter from '../classes/EventEmiter'
import type {
  Icomponent,
  IContainers,
  IProcessedComponent,
  IProcessedComponentProps,
  IProcessedContainer,
  UnresolvedRecord,
} from '../interfaces'
import { RootState } from '../redux/store'
import { processActions } from '../utils/processActions'
import processData from '../utils/processData'
import { getMinAmountProgressMessage } from '../utils/minAmountProgressMessage'
import { isHidePricesEnabled } from '../utils/hidePrices'
import maskPricesForDisplay from '../utils/maskPricesForDisplay'

const MIN_AMOUNT_IMPORT_ROUTE = 'MinAmount'

const resolveProps = (
  data: UnresolvedRecord,
  state: RootState,
  preserveTotal = false,
) => {
  const resolved = processData(data, { state })
  if (!isHidePricesEnabled(state)) return resolved
  return maskPricesForDisplay(resolved, preserveTotal)
}

const getCinnamonProps = <T = unknown>(
  props: UnresolvedRecord,
  state: RootState,
  eventEmitter: EventEmitter,
  preserveTotal = false,
): IProcessedComponentProps<T> => {
  const cinnamonProps = resolveProps(props, state, preserveTotal)
  return processActions(cinnamonProps, eventEmitter)
}

const mapComponent =
  (state: RootState, eventEmitter: EventEmitter) =>
  ({ props = {}, ...rest }: Icomponent): IProcessedComponent => {
    const isMinAmount = rest.importRoute === MIN_AMOUNT_IMPORT_ROUTE
    const resolvedProps = getCinnamonProps(
      props,
      state,
      eventEmitter,
      isMinAmount,
    )

    if (isMinAmount) {
      const message = getMinAmountProgressMessage(state, resolvedProps)
      if (message !== undefined) {
        resolvedProps.message = message
      }
    }

    return { ...rest, props: resolvedProps }
  }

const processDataValidated = (
  data: UnresolvedRecord | undefined = {},
  state: RootState,
) => resolveProps(data, state)

export const getCinnamonPropsContainer = (
  { header, body, footer, modals, uid }: IContainers,
  state: RootState,
  eventEmitter: EventEmitter,
): IProcessedContainer => {
  const toProcessedComponent = mapComponent(state, eventEmitter)
  return {
    uid,
    modals,
    header: {
      props: processDataValidated(header?.props, state),
      components:
        header?.components.map<IProcessedComponent>(toProcessedComponent) || [],
    },
    body: {
      props: processDataValidated(body?.props, state),
      components:
        body?.components.map<IProcessedComponent>(toProcessedComponent) || [],
    },
    footer: {
      props: processDataValidated(footer?.props, state),
      components:
        footer?.components.map<IProcessedComponent>(toProcessedComponent) || [],
    },
  }
}

export default getCinnamonProps
